import React, { memo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import classNames from 'classnames';

import styles from '../../DataExtractionTemplate.module.scss';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import EditableField from '../EditableField';
import {
  validate,
  NO_EQUIVALENT_VALUE,
} from '../../../components/editableFieldValidation';
import ConfirmationDialog from '../ConfirmationDialog';
import { useHidingDefaultFields } from '../../useHidingDefaultFields';
import { useCheckSuggestions } from '../../utils';
import { SparkleWithTooltip } from 'concepts/AutoDocExtraction/Sparkle/Sparkle';
import {
  Heading,
  Text,
  Icon,
  IconList,
  Link,
  FormControl,
  Checkbox,
} from 'components/core';
import {
  CustomFieldSupportedSuggestion,
  HiddenFieldCategory,
} from 'types/DataExtraction';

const RESERVED_FIELD_NAMES: string[] = [
  'sponsorship source',
  'country',
  'setting',
  'comments',
  "author's name",
  'institution',
  'email',
  'address',
];

type Props = {
  hasExtractedData?: boolean;
  useSuggestions: boolean;
  supportedSuggestions: CustomFieldSupportedSuggestion[];
};

const IdentificationPanel = ({
  hasExtractedData = false,
  useSuggestions,
  supportedSuggestions,
}: Props) => {
  const [modal, setModal] = useState<React.ReactElement | null>();
  const { getValues } = useFormContext();
  const additionalFields = useFieldArray<DataExtractionTemplateState>({
    name: 'identification.additionalFields',
  });

  const { handleChange, isChecked } = useHidingDefaultFields(
    HiddenFieldCategory.IDENTIFICATION
  );
  const MAX_LENGTH = 255;
  const sectionClassnames = classNames({
    [styles.Panel]: true,
    [styles.WithinAccordion]: true,
  });

  const checkSuggestions = useCheckSuggestions(
    supportedSuggestions,
    'identification'
  );

  return (
    <section className={sectionClassnames}>
      {modal}

      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Study details
        </Heading>
        <ul>
          <li>
            <FormControl
              actions={
                useSuggestions && (
                  <SparkleWithTooltip label="Sponsorship source" />
                )
              }
            >
              <Checkbox
                name="sponsorship_source"
                value="sponsorship_source"
                checked={isChecked('sponsorship_source')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Sponsorship source
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={useSuggestions && <SparkleWithTooltip label="Country" />}
            >
              <Checkbox
                name="country"
                value="country"
                checked={isChecked('country')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Country
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={useSuggestions && <SparkleWithTooltip label="Setting" />}
            >
              <Checkbox
                name="setting"
                value="setting"
                checked={isChecked('setting')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Setting
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="comments"
                value="comments"
                checked={isChecked('comments')}
                onChange={handleChange}
              >
                <Text variant="medium">Comments</Text>
              </Checkbox>
            </FormControl>
          </li>
        </ul>
      </div>

      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          {`Author's contact details`}
        </Heading>
        <ul>
          <li>
            <FormControl
              actions={useSuggestions && <SparkleWithTooltip label="Name" />}
            >
              <Checkbox
                name="name"
                value="name"
                checked={isChecked('name')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Name
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={
                useSuggestions && <SparkleWithTooltip label="Institution" />
              }
            >
              <Checkbox
                name="institution"
                value="institution"
                checked={isChecked('institution')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Institution
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={useSuggestions && <SparkleWithTooltip label="Email" />}
            >
              <Checkbox
                name="email"
                value="email"
                checked={isChecked('email')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Email
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={useSuggestions && <SparkleWithTooltip label="Address" />}
            >
              <Checkbox
                name="address"
                value="address"
                checked={isChecked('address')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Address
                </Text>
              </Checkbox>
            </FormControl>
          </li>
        </ul>
      </div>

      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Additional data
        </Heading>
        {additionalFields.fields.map((field, index) => {
          return (
            <EditableField
              key={field.id}
              index={index}
              name="identification.additionalFields"
              onDelete={() => {
                const { id } = getValues(
                  `identification.additionalFields.${index}`
                );

                if (id && hasExtractedData) {
                  setModal(
                    <ConfirmationDialog
                      onConfirm={() => additionalFields.remove(index)}
                      onClose={() => setModal(null)}
                    />
                  );
                } else {
                  additionalFields.remove(index);
                }
              }}
              validate={(value, existingFields) => {
                return validate(
                  value,
                  existingFields,
                  RESERVED_FIELD_NAMES,
                  NO_EQUIVALENT_VALUE,
                  MAX_LENGTH
                );
              }}
              suggestable={checkSuggestions}
            />
          );
        })}
        <Link
          onClick={() => additionalFields.append({ name: '' })}
          variant="primary"
          className={styles.AddButton}
        >
          <Icon icon={IconList.light.faPlus} /> Add additional data
        </Link>
      </div>
    </section>
  );
};

export default memo(IdentificationPanel);
